import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from '../hooks/use-toast';
import {
  Brain, Plus, TrendingUp, Sparkles, Target, BarChart3,
  Lightbulb, ArrowRight, Loader2, Map, Rocket, Award,
  CheckCircle, FileText, Download, Users, User, Trophy,
  GraduationCap, Play, Clock, ChevronRight, Eye, ArrowUpRight,
  Zap, BookOpen
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { trackPageView } from '../utils/analytics';
import DashboardThinkLab from '../components/DashboardThinkLab';
import IQIBadge from '../components/IQIBadge';
import IQIBreakdownModal from '../components/IQIBreakdownModal';
import ProfileCompletion from '../components/ProfileCompletion';
import { calculateSessionIQI } from '../utils/iqi';
import { SkeletonDashboard } from '../components/ui/skeleton';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Helpers ───────────────────────────────────────────────
const formatRelativeDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const getScoreColor = (score) => {
  if (score >= 8) return 'text-emerald-600';
  if (score >= 6) return 'text-violet-600';
  if (score >= 4) return 'text-blue-600';
  return 'text-slate-500';
};

const getScoreBg = (score) => {
  if (score >= 8) return 'bg-emerald-50 border-emerald-200';
  if (score >= 6) return 'bg-violet-50 border-violet-200';
  if (score >= 4) return 'bg-blue-50 border-blue-200';
  return 'bg-slate-50 border-slate-200';
};

// ─── Stat Card ─────────────────────────────────────────────
const StatCard = ({ icon: Icon, value, label, color, trend }) => (
  <Card data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`} className="bg-white border border-slate-100 shadow-sm hover:shadow-md transition-all duration-300">
    <CardContent className="p-5">
      <div className="flex items-center justify-between mb-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="w-5 h-5" />
        </div>
        {trend && (
          <span className="text-xs text-emerald-600 font-medium flex items-center gap-1">
            <TrendingUp className="w-3 h-3" /> {trend}
          </span>
        )}
      </div>
      <div className="text-3xl font-bold text-slate-900 tracking-tight">{value}</div>
      <div className="text-xs text-slate-500 font-medium mt-1">{label}</div>
    </CardContent>
  </Card>
);

// ─── Activity Row ──────────────────────────────────────────
const ActivityRow = ({ item, onViewReport, onResume, onNavigate }) => {
  const isSession = item._type === 'session';
  const score = isSession ? (item.oi_scores?.total_oi || item.oi_scores?.total) : (item.oi_total || item.oi_score);
  const hasScore = score && score > 0;
  const isComplete = isSession ? (item.completed || item.status === 'completed' || hasScore) : true;
  const title = item.title || item.paper_title || 'Untitled';

  return (
    <div
      data-testid={`activity-row-${item.id || item.evaluation_id}`}
      className="flex items-center gap-4 p-4 border-b border-slate-100 last:border-0 hover:bg-slate-50/80 transition-colors cursor-pointer group"
      onClick={() => {
        if (isSession && !isComplete) {
          onResume(item);
        } else if (isSession) {
          // Navigate to session view so user can see their work and evaluation
          onNavigate(`/session/${item.id}`);
        } else {
          onNavigate('/my-evaluations');
        }
      }}
    >
      {/* Icon */}
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
        isSession
          ? (isComplete ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600')
          : 'bg-violet-100 text-violet-600'
      }`}>
        {isSession ? <Brain className="w-5 h-5" /> : <FileText className="w-5 h-5" />}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-semibold text-slate-800 truncate group-hover:text-violet-700 transition-colors">
          {title}
        </h4>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-xs text-slate-400">{isSession ? 'Think Lab' : 'Evaluation'}</span>
          <span className="text-slate-300">·</span>
          <span className="text-xs text-slate-400">{formatRelativeDate(item.created_at)}</span>
          {!isComplete && (
            <>
              <span className="text-slate-300">·</span>
              <span className="text-xs text-amber-600 font-medium">In Progress</span>
            </>
          )}
        </div>
      </div>

      {/* Score */}
      <div className="flex-shrink-0 text-right">
        {hasScore ? (
          <div className={`text-lg font-bold ${getScoreColor(score)}`}>
            {score.toFixed(1)}
          </div>
        ) : (
          <Badge variant="outline" className="text-xs text-slate-400 border-slate-200">
            {isComplete ? 'No Score' : 'Pending'}
          </Badge>
        )}
      </div>

      {/* Action hint */}
      <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-violet-500 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
    </div>
  );
};

// ─── Main Dashboard Component ──────────────────────────────
const Dashboard = () => {
  const navigate = useNavigate();
  const { loginWithGoogle, isAuthenticated, loading: authLoading, user } = useAuth();

  const [userMetrics, setUserMetrics] = useState(null);
  const [userIdeas, setUserIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [evaluations, setEvaluations] = useState([]);
  const [showThinkLab, setShowThinkLab] = useState(false);
  const [selectedIQI, setSelectedIQI] = useState(null);
  const [loadingIQI, setLoadingIQI] = useState({});

  // Report Modal state
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedSessionReport, setSelectedSessionReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);

  // Achievements state
  const [achievements, setAchievements] = useState({ unlocked: [], locked: [], total_unlocked: 0, total_achievements: 0 });

  // Projection Modal state
  const [selectedIdea, setSelectedIdea] = useState(null);
  const [projection, setProjection] = useState(null);
  const [loadingProjection, setLoadingProjection] = useState(false);

  // Auto-open Think Lab
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('thinklab') === 'open') setShowThinkLab(true);
  }, []);

  // Google OAuth redirect
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes('session_id=')) {
      const sessionId = hash.split('session_id=')[1].split('&')[0];
      handleGoogleAuth(sessionId);
    }
  }, []);

  const handleGoogleAuth = async (sessionId) => {
    try {
      await axios.get('https://demobackend.emergentagent.com/auth/v1/env/oauth/session-data', {
        headers: { 'X-Session-ID': sessionId }
      });
      const result = await loginWithGoogle(sessionId);
      if (result.success) {
        window.history.replaceState({}, document.title, window.location.pathname);
      } else {
        navigate('/auth');
      }
    } catch {
      navigate('/auth');
    }
  };

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated && !window.location.hash.includes('session_id')) {
      navigate('/auth');
    }
  }, [isAuthenticated, authLoading]);

  // Track page view
  useEffect(() => {
    if (isAuthenticated) trackPageView('/dashboard');
  }, [isAuthenticated]);

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('if_auth_token');
        const config = {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true
        };

        const [metricsRes, sessionsRes, evalsRes, achievementsRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/metrics/user-dashboard`, config),
          axios.get(`${BACKEND_URL}/api/sessions?limit=50`, config),
          axios.get(`${BACKEND_URL}/api/evaluations/user`, config).catch(() => ({ data: { evaluations: [] } })),
          axios.get(`${BACKEND_URL}/api/achievements`, config).catch(() => ({ data: { unlocked: [], locked: [], total_unlocked: 0, total_achievements: 0 } }))
        ]);

        setUserMetrics(metricsRes.data);
        setUserIdeas(sessionsRes.data || []);
        setEvaluations(evalsRes.data?.evaluations || evalsRes.data || []);
        setAchievements(achievementsRes.data || { unlocked: [], locked: [], total_unlocked: 0, total_achievements: 0 });
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) fetchDashboardData();
  }, [isAuthenticated]);

  // Fetch projection
  const fetchProjection = async (sessionId) => {
    try {
      setLoadingProjection(true);
      const token = localStorage.getItem('if_auth_token');
      const config = { headers: token ? { Authorization: `Bearer ${token}` } : {}, withCredentials: true };
      const res = await axios.get(`${BACKEND_URL}/api/sessions/${sessionId}/projection`, config);
      setProjection(res.data);
    } catch (error) {
      if (error.response?.status === 404) {
        try {
          const session = userIdeas.find(s => s.id === sessionId);
          if (session) {
            const generateRes = await axios.post(`${BACKEND_URL}/api/sessions/${sessionId}/generate-projection`, {
              idea_title: session.title,
              idea_description: JSON.stringify(session),
              oi_score: session.oi_scores?.total_oi || 0,
              spark_percentage: session.msa_scores?.spark_pct || 0
            });
            setProjection(generateRes.data);
          }
        } catch { /* silent */ }
      }
    } finally {
      setLoadingProjection(false);
    }
  };

  // View report
  const handleViewReport = async (session) => {
    setSelectedSessionReport(null);
    setShowReportModal(true);
    setLoadingReport(true);
    try {
      const token = localStorage.getItem('if_auth_token');
      const config = { headers: token ? { Authorization: `Bearer ${token}` } : {}, withCredentials: true };
      let reportData = null;
      try {
        const res = await axios.get(`${BACKEND_URL}/api/sessions/${session.id}/report`, config);
        reportData = res.data;
      } catch (err) {
        if (err.response?.status === 404) {
          const genRes = await axios.post(`${BACKEND_URL}/api/sessions/${session.id}/generate-report`, {}, config);
          reportData = genRes.data;
        } else throw err;
      }
      setSelectedSessionReport({ ...session, report: reportData?.report || reportData?.structured_report || null });
    } catch {
      setSelectedSessionReport({ ...session, report: null, error: 'Could not generate detailed report' });
    } finally {
      setLoadingReport(false);
    }
  };

  // IQI calculation
  const handleCalculateIQI = async (sessionId, event) => {
    event.stopPropagation();
    try {
      setLoadingIQI(prev => ({ ...prev, [sessionId]: true }));
      const result = await calculateSessionIQI(sessionId);
      setUserIdeas(prev => prev.map(idea =>
        idea.id === sessionId ? { ...idea, iqi_scores: result.iqi_scores } : idea
      ));
      setSelectedIQI(result.iqi_scores);
    } catch {
      alert('Failed to calculate IQI. Please try again.');
    } finally {
      setLoadingIQI(prev => ({ ...prev, [sessionId]: false }));
    }
  };

  // ─── Computed Data ─────────────────────────────────────────
  const inProgressSessions = useMemo(() =>
    userIdeas.filter(s => 
      !s.completed && 
      s.status !== 'completed' && 
      !(s.oi_scores?.total_oi > 0) &&
      !(s.oi_scores?.total > 0)
    ).slice(0, 3),
    [userIdeas]
  );

  const recentActivity = useMemo(() => {
    const items = [
      ...userIdeas.map(s => ({ ...s, _type: 'session' })),
      ...(Array.isArray(evaluations) ? evaluations : []).map(e => ({ ...e, _type: 'evaluation' }))
    ];
    return items.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 10);
  }, [userIdeas, evaluations]);

  // ─── Render ────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-50" data-testid="dashboard-page">
      <div className="max-w-7xl mx-auto px-6 py-8">

        {/* ── Header ─────────────────────────────────────── */}
        <div className="mb-8" data-testid="dashboard-header">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-slate-900">
                {user ? `Welcome back, ${user.name?.split(' ')[0]}` : 'Dashboard'}
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                Track your creative originality journey
              </p>
            </div>
            <Button
              data-testid="new-session-btn"
              onClick={() => navigate('/session/new')}
              className="bg-violet-600 hover:bg-violet-700 text-white shadow-md"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Session
            </Button>
          </div>

          {/* Profile Completion */}
          {user && (
            <div className="mt-4">
              <ProfileCompletion user={user} />
            </div>
          )}
        </div>

        {loading ? (
          <SkeletonDashboard />
        ) : (
          <>
            {/* ── Metrics Bar ───────────────────────────────── */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="metrics-bar">
              <StatCard
                icon={Lightbulb}
                value={userMetrics?.total_sessions || 0}
                label="Total Sessions"
                color="bg-violet-100 text-violet-600"
              />
              <StatCard
                icon={CheckCircle}
                value={userMetrics?.completed_sessions || 0}
                label="Completed"
                color="bg-emerald-100 text-emerald-600"
              />
              <StatCard
                icon={BarChart3}
                value={userMetrics?.avg_oi_score || 0}
                label="Avg OI Score"
                color="bg-blue-100 text-blue-600"
              />
              <StatCard
                icon={Sparkles}
                value={`${userMetrics?.avg_spark_percentage || 0}%`}
                label="Avg Spark"
                color="bg-amber-100 text-amber-600"
              />
            </div>

            {/* ── Main Two-Column Layout ─────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

              {/* ── Left Column (8 cols) ─────────────────────── */}
              <div className="lg:col-span-8 space-y-6">

                {/* In Progress Sessions */}
                {inProgressSessions.length > 0 && (
                  <div data-testid="in-progress-section">
                    <div className="flex items-center gap-2 mb-3">
                      <Clock className="w-4 h-4 text-amber-500" />
                      <h2 className="text-sm font-semibold text-slate-800 uppercase tracking-wider">
                        Continue Where You Left Off
                      </h2>
                      <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs">
                        {inProgressSessions.length}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {inProgressSessions.map(session => (
                        <Card
                          key={session.id}
                          data-testid={`resume-card-${session.id}`}
                          className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 hover:shadow-md transition-all cursor-pointer group"
                          onClick={() => navigate(`/session/${session.id}`)}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <Brain className="w-4 h-4 text-amber-600" />
                              <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs">
                                In Progress
                              </Badge>
                            </div>
                            <h3 className="text-sm font-semibold text-slate-800 line-clamp-1 mb-1">
                              {session.title || 'Untitled Session'}
                            </h3>
                            <p className="text-xs text-slate-500 mb-3">{formatRelativeDate(session.created_at)}</p>
                            <Button
                              size="sm"
                              className="w-full bg-amber-500 hover:bg-amber-600 text-white text-xs"
                            >
                              <Play className="w-3 h-3 mr-1" /> Resume Session
                            </Button>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recent Activity */}
                <div data-testid="recent-activity-section">
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-sm font-semibold text-slate-800 uppercase tracking-wider">
                      Recent Activity
                    </h2>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs text-slate-500 hover:text-violet-600"
                      onClick={() => navigate('/my-work')}
                      data-testid="view-all-work-btn"
                    >
                      View All <ArrowRight className="w-3 h-3 ml-1" />
                    </Button>
                  </div>
                  <Card className="border border-slate-200 shadow-sm overflow-hidden">
                    {recentActivity.length > 0 ? (
                      recentActivity.map(item => (
                        <ActivityRow
                          key={`${item._type}-${item.id || item.evaluation_id}`}
                          item={item}
                          onViewReport={handleViewReport}
                          onResume={(s) => navigate(`/session/${s.id}`)}
                          onNavigate={navigate}
                        />
                      ))
                    ) : (
                      <div className="p-8 text-center">
                        <Lightbulb className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                        <p className="text-sm text-slate-500">No activity yet. Start your first session!</p>
                      </div>
                    )}
                  </Card>
                </div>
              </div>

              {/* ── Right Column (4 cols) ────────────────────── */}
              <div className="lg:col-span-4 space-y-6">

                {/* Quick Actions */}
                <div data-testid="quick-actions-section">
                  <h2 className="text-sm font-semibold text-slate-800 uppercase tracking-wider mb-3">
                    Quick Actions
                  </h2>
                  <div className="space-y-3">
                    <Card
                      data-testid="action-new-session"
                      className="bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white border-0 shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all cursor-pointer group"
                      onClick={() => navigate('/session/new')}
                    >
                      <CardContent className="p-5">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                            <Zap className="w-5 h-5" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold text-sm">New Think Lab Session</h3>
                            <p className="text-xs text-white/70 mt-0.5">Start a structured thinking session</p>
                          </div>
                          <ArrowUpRight className="w-4 h-4 text-white/50 group-hover:text-white transition-colors" />
                        </div>
                      </CardContent>
                    </Card>

                    <Card
                      data-testid="action-evaluate"
                      className="bg-white border border-slate-200 shadow-sm hover:shadow-md hover:border-blue-300 transition-all cursor-pointer group"
                      onClick={() => navigate('/quick-evaluate')}
                    >
                      <CardContent className="p-5">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <FileText className="w-5 h-5 text-blue-600" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold text-sm text-slate-800">Evaluate a Paper</h3>
                            <p className="text-xs text-slate-500 mt-0.5">AI-powered originality analysis</p>
                          </div>
                          <ArrowUpRight className="w-4 h-4 text-slate-300 group-hover:text-blue-500 transition-colors" />
                        </div>
                      </CardContent>
                    </Card>

                    <Card
                      data-testid="action-challenges"
                      className="bg-white border border-slate-200 shadow-sm hover:shadow-md hover:border-emerald-300 transition-all cursor-pointer group"
                      onClick={() => navigate('/challenges')}
                    >
                      <CardContent className="p-5">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                            <Target className="w-5 h-5 text-emerald-600" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold text-sm text-slate-800">Join a Challenge</h3>
                            <p className="text-xs text-slate-500 mt-0.5">Compete in innovation challenges</p>
                          </div>
                          <ArrowUpRight className="w-4 h-4 text-slate-300 group-hover:text-emerald-500 transition-colors" />
                        </div>
                      </CardContent>
                    </Card>

                    {user?.user_role === 'educator' && (
                    <Card
                      data-testid="action-educator"
                      className="bg-white border border-slate-200 shadow-sm hover:shadow-md hover:border-purple-300 transition-all cursor-pointer group"
                      onClick={() => navigate('/educator')}
                    >
                      <CardContent className="p-5">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                            <GraduationCap className="w-5 h-5 text-purple-600" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold text-sm text-slate-800">Educator Mode</h3>
                            <p className="text-xs text-slate-500 mt-0.5">Batch evaluate student papers</p>
                          </div>
                          <ArrowUpRight className="w-4 h-4 text-slate-300 group-hover:text-purple-500 transition-colors" />
                        </div>
                      </CardContent>
                    </Card>
                    )}
                  </div>
                </div>

                {/* Think Lab Widget */}
                <div data-testid="think-lab-widget">
                  <h2 className="text-sm font-semibold text-slate-800 uppercase tracking-wider mb-3">
                    Think Lab
                  </h2>
                  {showThinkLab ? (
                    <div>
                      <DashboardThinkLab />
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full mt-2 text-xs text-slate-500"
                        onClick={() => setShowThinkLab(false)}
                      >
                        Collapse Think Lab
                      </Button>
                    </div>
                  ) : (
                    <Card
                      className="bg-slate-900 text-white border-0 shadow-xl hover:shadow-2xl transition-all cursor-pointer group overflow-hidden relative"
                      onClick={() => setShowThinkLab(true)}
                    >
                      <div className="absolute inset-0 bg-gradient-to-br from-violet-600/20 to-fuchsia-600/20 opacity-0 group-hover:opacity-100 transition-opacity" />
                      <CardContent className="p-6 relative">
                        <Brain className="w-8 h-8 text-violet-400 mb-3" />
                        <h3 className="font-semibold text-base mb-1">AI Workspace</h3>
                        <p className="text-xs text-slate-400 mb-4">
                          Brainstorming, tasks, design sprints — all in one place
                        </p>
                        <Button
                          size="sm"
                          className="bg-white/10 hover:bg-white/20 text-white border border-white/20 text-xs"
                        >
                          <Rocket className="w-3 h-3 mr-1" /> Open Workspace
                        </Button>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Achievements */}
                {achievements.total_achievements > 0 && (
                  <div data-testid="achievements-section">
                    <h2 className="text-sm font-semibold text-slate-800 uppercase tracking-wider mb-3">
                      Achievements
                    </h2>
                    <Card className="border border-slate-200 shadow-sm">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-2xl font-bold text-slate-900">{achievements.total_unlocked}</span>
                          <span className="text-xs text-slate-400">/ {achievements.total_achievements} badges</span>
                        </div>
                        {achievements.unlocked.length > 0 ? (
                          <div className="space-y-2">
                            {achievements.unlocked.slice(0, 4).map((a, i) => (
                              <div key={i} className={`flex items-center gap-2 p-2 rounded-lg text-xs ${
                                a.tier === 'gold' ? 'bg-amber-50 text-amber-800' :
                                a.tier === 'silver' ? 'bg-slate-100 text-slate-700' :
                                'bg-violet-50 text-violet-700'
                              }`}>
                                <span className="text-base">{a.tier === 'gold' ? '🏆' : a.tier === 'silver' ? '🥈' : '🥉'}</span>
                                <div>
                                  <div className="font-semibold">{a.achievement_name}</div>
                                  <div className="text-[10px] opacity-70">{a.achievement_description}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-slate-400">Complete sessions and evaluations to earn badges</p>
                        )}
                        {achievements.locked.length > 0 && (
                          <p className="text-xs text-slate-400 mt-2 text-center">
                            {achievements.locked.length} more to unlock
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Score Insight */}
                {userMetrics?.avg_oi_score > 0 && (
                  <Card className="border border-slate-200 shadow-sm" data-testid="score-insight">
                    <CardContent className="p-5">
                      <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-emerald-500" />
                        Your OI Performance
                      </h3>
                      <div className="flex items-end gap-3">
                        <div className="text-4xl font-bold text-emerald-600">
                          {userMetrics.avg_oi_score}
                        </div>
                        <div className="text-xs text-slate-500 pb-1">
                          average across {userMetrics.completed_sessions || 0} sessions
                        </div>
                      </div>
                      <div className="mt-3 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 rounded-full transition-all"
                          style={{ width: `${(userMetrics.avg_oi_score / 10) * 100}%` }}
                        />
                      </div>
                      <div className="flex justify-between mt-1">
                        <span className="text-xs text-slate-400">0</span>
                        <span className="text-xs text-slate-400">10</span>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── Report Modal ──────────────────────────────────── */}
      {showReportModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="report-modal">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
            <div className="sticky top-0 bg-slate-900 p-6 text-white">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-xl font-bold mb-1">Session Report</h2>
                  <p className="text-slate-400 text-sm">{selectedSessionReport?.title || 'Loading...'}</p>
                </div>
                <Button variant="ghost" size="sm" className="text-white hover:bg-white/20"
                  onClick={() => { setShowReportModal(false); setSelectedSessionReport(null); }}>
                  ✕
                </Button>
              </div>
              {selectedSessionReport && !loadingReport && (
                <div className="mt-4 flex items-center gap-4">
                  <div className="bg-white/10 rounded-lg px-4 py-2 text-center">
                    <div className="text-2xl font-bold">{(selectedSessionReport.oi_scores?.total || selectedSessionReport.oi_scores?.total_oi || 0).toFixed(1)}</div>
                    <div className="text-xs opacity-70">OI Score</div>
                  </div>
                  <div className="bg-white/10 rounded-lg px-4 py-2 text-center">
                    <div className="text-2xl font-bold">{(selectedSessionReport.msa_scores?.spark_pct || 0).toFixed(0)}%</div>
                    <div className="text-xs opacity-70">Spark</div>
                  </div>
                </div>
              )}
            </div>
            <div className="overflow-y-auto max-h-[calc(90vh-180px)] p-6">
              {loadingReport ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <Loader2 className="w-10 h-10 animate-spin text-violet-600 mb-4" />
                  <p className="text-slate-500 text-sm">Loading session report...</p>
                </div>
              ) : selectedSessionReport ? (
                <div className="space-y-6">
                  {selectedSessionReport.error && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800 text-sm">
                      {selectedSessionReport.error}
                    </div>
                  )}
                  {selectedSessionReport.report?.summary && (
                    <div>
                      <h3 className="text-sm font-semibold text-slate-800 mb-2">Summary</h3>
                      <p className="text-sm text-slate-600 bg-slate-50 p-4 rounded-lg">{selectedSessionReport.report.summary}</p>
                    </div>
                  )}
                  {selectedSessionReport.oi_scores && (
                    <div>
                      <h3 className="text-sm font-semibold text-slate-800 mb-3">OI Breakdown</h3>
                      <div className="grid md:grid-cols-2 gap-3">
                        {[
                          { label: 'Novelty', key: 'novelty', color: 'violet', analysis: 'novelty_analysis' },
                          { label: 'Resonance', key: 'resonance', color: 'blue', analysis: 'resonance_analysis' },
                          { label: 'Synthesis', key: 'synthesis', color: 'emerald', analysis: 'synthesis_analysis' },
                          { label: 'Ethical Anchoring', key: 'ethical', color: 'amber', analysis: 'ethical_analysis' },
                        ].map(dim => (
                          <div key={dim.key} className="border border-slate-200 rounded-lg p-4">
                            <div className="flex justify-between items-center">
                              <span className="text-sm font-medium text-slate-700">{dim.label}</span>
                              <span className={`text-xl font-bold text-${dim.color}-600`}>
                                {(selectedSessionReport.oi_scores[dim.key] || 0).toFixed(1)}
                              </span>
                            </div>
                            {selectedSessionReport.report?.[dim.analysis] && (
                              <p className="text-xs text-slate-500 mt-2">{selectedSessionReport.report[dim.analysis]}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {selectedSessionReport.report?.key_insights?.length > 0 && (
                    <div className="bg-violet-50 rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-violet-900 mb-2">Key Insights</h4>
                      <ul className="text-sm text-slate-700 space-y-1.5">
                        {selectedSessionReport.report.key_insights.map((insight, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <span className="text-violet-400 mt-0.5 text-xs">&#9670;</span> {insight}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {selectedSessionReport.report?.recommendations?.length > 0 && (
                    <div className="bg-blue-50 rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-blue-900 mb-2">Recommendations</h4>
                      <ul className="text-sm text-slate-700 space-y-1.5">
                        {selectedSessionReport.report.recommendations.map((rec, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <ArrowRight className="w-3 h-3 text-blue-400 mt-1 flex-shrink-0" /> {rec}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
            <div className="border-t p-4 bg-slate-50 flex justify-end gap-2">
              {selectedSessionReport?.oi_scores?.total > 0 && (
                <Button
                  data-testid="download-pdf-btn"
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('if_auth_token');
                      const response = await fetch(`${BACKEND_URL}/api/sessions/${selectedSessionReport.id}/report-pdf`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                      });
                      if (!response.ok) throw new Error('Failed to download PDF');
                      const blob = await response.blob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `IF_Report_${selectedSessionReport.title?.slice(0, 20) || 'session'}.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      window.URL.revokeObjectURL(url);
                      document.body.removeChild(a);
                      toast({ title: "PDF Downloaded", description: "Report also sent to your email" });
                    } catch (error) {
                      alert(error.message || 'Failed to download PDF');
                    }
                  }}
                  className="bg-violet-600 hover:bg-violet-700 text-white"
                >
                  <Download className="w-4 h-4 mr-2" /> Download PDF
                </Button>
              )}
              <Button variant="outline" onClick={() => { setShowReportModal(false); setSelectedSessionReport(null); }}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── IQI Breakdown Modal ───────────────────────────── */}
      {selectedIQI && (
        <IQIBreakdownModal
          iqi_scores={selectedIQI}
          onClose={() => setSelectedIQI(null)}
          contentType="session"
        />
      )}
    </div>
  );
};

export default Dashboard;
